// ============================================================
// OLIVIUM · Trust Overview — post-login trust dashboard.
//
// Shows the operator a calm, clear view of their trust posture:
// session state, device trust, recent protected actions, alerts,
// and chain integrity. Uses only backend-supported data.
// ============================================================

import { useNavigate } from 'react-router-dom'
import { SectionHeader } from '@square/ui'
import MetricCard from '../../components/MetricCard'
import EmptyState from '../../components/EmptyState'
import { useAuditFeed } from '../../hooks/useAuditFeed'
import { useSessions } from '../../hooks/useSessions'
import { useChainIntegrity } from '../../hooks/useChainIntegrity'
import { useOliviumAlerts } from '../../hooks/useOliviumAlerts'
import { SHELL_TOKENS } from '../../shell/styles'

export default function OliviumTrust() {
  const navigate = useNavigate()
  const { events, stats } = useAuditFeed()
  const { sessions } = useSessions()
  const { valid: chainValid, checked: chainCount } = useChainIntegrity()
  const { alerts } = useOliviumAlerts()

  const activeSessions = sessions.filter((s: { status: string }) => s.status === 'active')
  const openAlerts = alerts.filter((a: { status: string }) => a.status === 'open')
  const recentEvents = events.slice(0, 6)

  return (
    <div style={{ maxWidth: 920, margin: '0 auto' }}>
      {/* Header — Brand Guide v2: OLV mark in purple substrate, page title
          32px weight 200 letter-spacing -1px. No gradient text. */}
      <div style={{ marginBottom: 28, display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            background: `${SHELL_TOKENS.purple}26`,
            border: `1px solid ${SHELL_TOKENS.purple}40`,
            boxShadow: `0 0 12px ${SHELL_TOKENS.purple}1a, inset 0 0 8px ${SHELL_TOKENS.purple}0d`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 800,
            color: SHELL_TOKENS.purple,
            fontFamily: 'var(--sc-font-mono, var(--font-mono))',
            flexShrink: 0,
            marginTop: 6,
          }}
        >
          OLV
        </div>
        <SectionHeader
          as="page"
          accent={SHELL_TOKENS.purple}
          kicker="OLIVIUM · TRUST OVERVIEW"
          title="Trust Posture"
          description="Signed-in operator sessions are hash-chained into olv_audit_trail and reflected here. Metrics below are derived from live feeds when available and fall back to contracted policy otherwise."
          style={{ flex: 1, marginBottom: 0 }}
        />
      </div>

      {/* Posture cards */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: 10,
        marginBottom: 24,
      }}>
        <MetricCard
          label="Chain"
          value={chainValid ? 'Verified' : 'Break detected'}
          sub={`${chainCount} entries · SHA-256`}
          tone={chainValid ? 'default' : 'critical'}
        />
        <MetricCard
          label="Sessions"
          value={activeSessions.length}
          sub={activeSessions.length === 1 ? 'active session' : 'active sessions'}
        />
        <MetricCard
          label="Alerts"
          value={openAlerts.length}
          sub={openAlerts.length === 0 ? 'no open alerts' : 'require attention'}
          tone={openAlerts.length > 0 ? 'warning' : 'default'}
        />
        <MetricCard
          label="Events"
          value={stats.total}
          sub={`${stats.criticals} critical · ${stats.denials} denied`}
          tone={stats.criticals > 0 ? 'critical' : 'default'}
        />
      </section>

      {/* Trust pillars — these show the CONTRACT (what OLIVIUM is supposed
          to enforce). Only the Audit Chain row is currently bound to a live
          check via useChainIntegrity. Everything else is policy, not health. */}
      <section style={{ marginBottom: 24 }}>
        <SectionHeader
          accent={SHELL_TOKENS.purple}
          kicker="TRUST CONTRACT · POLICIES"
          style={{ marginBottom: 12 }}
        />
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 10,
        }}>
          {[
            // Only this row is live-verified.
            { label: 'Audit Chain', value: chainValid ? 'Intact' : 'Compromised', color: chainValid ? SHELL_TOKENS.green : SHELL_TOKENS.red, detail: 'Hash-chained immutable log · live', verified: true },
            // The rest describe contracted policy; no live health check is wired.
            { label: 'Rate Limiting',       value: 'Policy set',        color: SHELL_TOKENS.inkMuted, detail: '20/15min by IP · 5/15min by email',     verified: false },
            { label: 'RLS Coverage',        value: 'Enforced in schema', color: SHELL_TOKENS.inkMuted, detail: 'Tables define RLS · runtime unchecked',  verified: false },
            { label: 'Session Governance',  value: 'Policy set',        color: SHELL_TOKENS.inkMuted, detail: 'Device trust + fingerprinting (policy)',verified: false },
            { label: 'Alert Delivery',      value: 'Policy set',        color: SHELL_TOKENS.inkMuted, detail: 'Criticals route to Slack (policy)',     verified: false },
            { label: 'Admin Gating',        value: 'minLevel: 80',      color: SHELL_TOKENS.inkMuted, detail: 'Role-based via OLIVIUM roles (policy)', verified: false },
          ].map((pillar) => (
            <div
              key={pillar.label}
              style={{
                padding: '14px 16px',
                borderRadius: 12,
                background: SHELL_TOKENS.surface,
                border: `1px solid ${SHELL_TOKENS.border}`,
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 6,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: SHELL_TOKENS.ink }}>
                  {pillar.label}
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  fontSize: 10,
                  fontFamily: 'var(--font-mono)',
                  color: pillar.color,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                }}>
                  <span style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: pillar.color,
                  }} />
                  {pillar.value}
                </div>
              </div>
              <div style={{
                fontSize: 10,
                color: SHELL_TOKENS.inkMuted,
                fontFamily: 'var(--font-mono)',
              }}>
                {pillar.detail}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Recent protected actions */}
      <section style={{ marginBottom: 24 }}>
        <SectionHeader
          accent={SHELL_TOKENS.purple}
          kicker="RECENT PROTECTED ACTIONS"
          style={{ marginBottom: 12 }}
        />
        {recentEvents.length === 0 ? (
          <EmptyState
            title="No recent events"
            body="Protected actions will appear here as they are recorded in the audit trail."
          />
        ) : (
          <div style={{
            borderRadius: 12,
            border: `1px solid ${SHELL_TOKENS.border}`,
            background: SHELL_TOKENS.surface,
            overflow: 'hidden',
          }}>
            {recentEvents.map((event: { id: string; action?: string; event_type?: string; actor_id?: string; outcome?: string; severity?: string; created_at?: string }, i: number) => (
              <div
                key={event.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 16px',
                  borderBottom: i === recentEvents.length - 1 ? 'none' : `1px solid ${SHELL_TOKENS.border}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background:
                      event.severity === 'critical' ? SHELL_TOKENS.red :
                      event.severity === 'warning' ? SHELL_TOKENS.amber :
                      SHELL_TOKENS.green,
                    flexShrink: 0,
                  }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: SHELL_TOKENS.ink }}>
                      {event.action || event.event_type || 'event'}
                    </div>
                    <div style={{ fontSize: 10, color: SHELL_TOKENS.inkMuted, fontFamily: 'var(--font-mono)' }}>
                      {event.actor_id ?? 'system'} · {event.outcome ?? '—'}
                    </div>
                  </div>
                </div>
                <div style={{
                  fontSize: 9,
                  color: SHELL_TOKENS.inkFaint,
                  fontFamily: 'var(--font-mono)',
                }}>
                  {event.created_at ? new Date(event.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* What OLIVIUM is designed to govern — framed as a capability statement,
          not a real-time assertion. Dots are neutral to avoid implying each
          item has just been checked and is green. */}
      <section style={{ marginBottom: 24 }}>
        <SectionHeader
          accent={SHELL_TOKENS.purple}
          kicker="OLIVIUM CAPABILITIES"
          style={{ marginBottom: 12 }}
        />
        <div style={{
          borderRadius: 12,
          border: `1px solid ${SHELL_TOKENS.border}`,
          background: SHELL_TOKENS.surface,
          padding: '16px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}>
          {[
            { label: 'Session lifecycle', detail: 'Governs login, logout, device trust, and inactivity timeouts when the session edge function is reachable.' },
            { label: 'Audit chain',       detail: 'Hash-chains protected actions into olv_audit_trail. Verify-chain is exposed via /api/olivium/verify-chain.' },
            { label: 'Access control',    detail: 'Role-based gating via OLIVIUM roles. Admin operations require level 80+ by policy.' },
            { label: 'Rate limiting',     detail: 'Rate limits are applied at the login, API, and admin endpoints by IP and identity.' },
            { label: 'Alert delivery',    detail: 'Critical events route to Slack and the alert queue when delivery is configured.' },
          ].map((item) => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{
                width: 5, height: 5, borderRadius: '50%',
                background: SHELL_TOKENS.inkMuted,
                marginTop: 5, flexShrink: 0,
              }} />
              <div>
                <span style={{ fontSize: 12, fontWeight: 700, color: SHELL_TOKENS.ink }}>{item.label}</span>
                <span style={{ fontSize: 11, color: SHELL_TOKENS.inkMuted, fontFamily: 'var(--font-mono)', marginLeft: 8 }}>{item.detail}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Navigation */}
      <section>
        <SectionHeader
          accent={SHELL_TOKENS.purple}
          kicker="EXPLORE"
          style={{ marginBottom: 12 }}
        />
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 10,
        }}>
          {[
            { label: 'Signal',    path: '/square/olivium/feed',     glyph: '◉', color: SHELL_TOKENS.cyan,   hint: 'Live audit feed' },
            { label: 'Presence',  path: '/square/olivium/sessions', glyph: '◎', color: SHELL_TOKENS.green,  hint: 'Active sessions' },
            { label: 'Integrity', path: '/square/olivium/chain',    glyph: '◈', color: SHELL_TOKENS.purple, hint: 'Chain verification' },
            { label: 'Alerts',    path: '/square/olivium/alerts',   glyph: '◆', color: SHELL_TOKENS.red,    hint: 'Trust alerts' },
          ].map((tile) => (
            <button
              key={tile.label}
              type="button"
              onClick={() => navigate(tile.path)}
              style={{
                padding: '16px 14px',
                borderRadius: 12,
                background: SHELL_TOKENS.surface,
                border: `1px solid ${SHELL_TOKENS.border}`,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = `${tile.color}40`
                e.currentTarget.style.background = `${tile.color}08`
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = SHELL_TOKENS.border
                e.currentTarget.style.background = SHELL_TOKENS.surface
              }}
            >
              <div style={{
                fontSize: 18,
                color: `${tile.color}aa`,
                fontFamily: 'var(--font-brand)',
                marginBottom: 8,
              }}>
                {tile.glyph}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: SHELL_TOKENS.ink, marginBottom: 2 }}>
                {tile.label}
              </div>
              <div style={{ fontSize: 10, color: SHELL_TOKENS.inkMuted, fontFamily: 'var(--font-mono)' }}>
                {tile.hint}
              </div>
            </button>
          ))}
        </div>
      </section>
    </div>
  )
}
